// Secure API service with CSRF protection and proper error handling
import { getAuthTokens } from '@/utils/auth'
import { CSRFProtection, sanitizeInput } from '@/utils/security'
import { authService } from './authService'
import { useAppStore } from '@/stores/app.store'

interface ApiRequestOptions extends RequestInit {
  requireAuth?: boolean
  skipCSRF?: boolean
  timeout?: number
}

interface ApiResponse<T = any> {
  data: T
  success: boolean
  message?: string
  errors?: string[]
}

class SecureApiService {
  private baseUrl: string
  private csrfToken: string | null = null

  constructor(baseUrl: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api') {
    this.baseUrl = baseUrl
  }

  private async refreshAccessToken(): Promise<string | null> {
  try {
    const { refreshToken } = getAuthTokens()
    if (!refreshToken) throw new Error('No refresh token found')
    const tokens = await authService.refreshToken()
    // Update tokens in Zustand
    useAppStore.getState().setTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? refreshToken,
      expiresIn: tokens.expiresIn ?? null,
    })
    return tokens.accessToken
  } catch (error) {
    // console.error('[Auth Refresh] Token refresh failed:', error)
    // useAppStore.getState().setTokens(null)
    return null
  }
}

  private async getCSRFToken(): Promise<string> {
    if (!this.csrfToken) {
      this.csrfToken = CSRFProtection.generateToken()
    }
    return this.csrfToken
  }

  private async validateCSRFToken(token: string): Promise<boolean> {
    return CSRFProtection.validateToken(token)
  }

  private sanitizeHeaders(headers: HeadersInit = {}): Record<string, string> {
    const sanitized: Record<string, string> = {}

    Object.entries(headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        sanitized[sanitizeInput(key)] = sanitizeInput(value)
      } else {
        sanitized[key] = String(value)
      }
    })

    return sanitized
  }


  private async makeRequest<T>(
    endpoint: string,
    options: ApiRequestOptions = {},
    retryAttempt = false,
  ): Promise<ApiResponse<T>> {
    const {
      requireAuth = true,
      skipCSRF = false,
      timeout: providedTimeout,
      ...fetchOptions
    } = options

    const { accessToken: token } = getAuthTokens()
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData
    const isBlob = typeof Blob !== 'undefined' && fetchOptions.body instanceof Blob
    const timeout = providedTimeout ?? (isFormData || isBlob ? 120000 : 10000)

    const headers: Record<string, string> = {
      ...this.sanitizeHeaders(fetchOptions.headers),
    }

    if (!isFormData && !isBlob) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    } else if (headers['Content-Type']) {
      delete headers['Content-Type']
    }

    // Attach auth header
    if (requireAuth) {
      if (!token) throw new Error('Authentication required')
      headers['Authorization'] = `Bearer ${token}`
    }

    // CSRF
    if (!skipCSRF && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method || 'GET')) {
      const csrfToken = await this.getCSRFToken()
      headers['X-CSRF-Token'] = csrfToken
    }

    headers['X-Requested-With'] = 'XMLHttpRequest'

    let sanitizedBody = fetchOptions.body
    // if (fetchOptions.body && typeof fetchOptions.body === 'string') {
    //   try {
    //     const parsed = JSON.parse(fetchOptions.body)
    //     sanitizedBody = JSON.stringify(this.sanitizeObject(parsed))
    //   } catch {
    //     sanitizedBody = sanitizeInput(fetchOptions.body as string)
    //   }
    // }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...fetchOptions,
        headers,
        body: sanitizedBody,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (response.status === 401 && !retryAttempt) {
        // Token expired → refresh & retry
        const newToken = await this.refreshAccessToken()
        if (newToken) {
          return this.makeRequest<T>(endpoint, options, true)
        } else {
          throw new Error('Session expired. Please log in again.')
        }
      }

      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response)
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await this.parseResponse<T>(response)
      return {
        data,
        success: true,
        message: response.headers.get('X-Message') || undefined,
      }
    } catch (error: any) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') throw new Error('Request timeout. Please try again.')
      throw this.sanitizeError(error)
    }
  }


  private sanitizeObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj
    }

    if (typeof obj === 'string') {
      return sanitizeInput(obj)
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item))
    }

    if (typeof obj === 'object') {
      const sanitized: any = {}
      for (const [key, value] of Object.entries(obj)) {
        sanitized[sanitizeInput(key)] = this.sanitizeObject(value)
      }
      return sanitized
    }

    return obj
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      return await response.json()
    }

    if (contentType?.includes('text/')) {
      const text = await response.text()
      return sanitizeInput(text) as T
    }

    return await response.blob() as T
  }

  private async parseErrorResponse(response: Response): Promise<{ message: string; errors?: string[] }> {
    try {
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        const errorData = await response.json()

        // Sanitize error message to prevent information disclosure
        let message = 'An error occurred'
        if (errorData.message && typeof errorData.message === 'string') {
          message = sanitizeInput(errorData.message)
        }

        return {
          message,
          errors: errorData.errors?.map((error: string) =>
            sanitizeInput(error)
          )
        }
      }
    } catch {
      // Fallback to generic error message
    }

    // Return sanitized error message
    const message = sanitizeInput(response.statusText || 'An error occurred')

    return { message }
  }

  private sanitizeError(error: Error): Error {
    // Remove stack traces and sensitive information
    const message = sanitizeInput(error.message)
    const sanitizedError = new Error(message)

    // Don't include stack trace in production
    if (import.meta.env.PROD) {
      delete (sanitizedError as any).stack
    }

    return sanitizedError
  }


  private getAuthToken(): string | null {
    return localStorage.getItem('token')
  }

  // Public API methods
  async get<T>(endpoint: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async postFormData<T>(endpoint: string, formData: FormData, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      headers: {
        ...options.headers,
      },
    })
  }

  async putFormData<T>(endpoint: string, formData: FormData, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: formData,
      headers: {
        ...options.headers,
      },
    })
  }

  async put<T>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'DELETE' })
  }

  // File upload with additional security
  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(sanitizeInput(key), sanitizeInput(String(value)))
      })
    }

    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
    })
  }
}

export const secureApiService = new SecureApiService()
export default secureApiService
